<script setup lang="ts">
import { BaseButton } from '@/components/base'
import { TimeFormatType, TimeType } from '@/enum'
import {
  DEFAULT_DATETIME_PICKER_HEIGHT,
  DEFAULT_JOB_START_TIME,
  HOURS_IN_DAY,
  MINUTES_IN_HOUR,
  TIME_FORMAT_24_HRS
} from '@/utils/constants'
import moment from 'moment'
import { computed, nextTick, onMounted, ref, watch, watchPostEffect } from 'vue'
import FormInputField from './FormInputField.vue'

const props = defineProps<{
  disabled?: boolean
  readonly?: boolean
  minimumStartTime?: string
  maximumStartTime?: string
  format?: TimeFormatType
  defaultStartTime?: string
  disablePastTime?: boolean
}>()

const boundValue = defineModel<any>({ default: '' })

const timepickerInputField = ref()
const timepickerDropdown = ref()
const hourRef = ref()
const minuteRef = ref()
const selectedTime = ref()
const isTimePickerOpen = ref(false)
const selectedHour = ref()
const selectedMinute = ref()
const timeType = ref(TimeType.AM)
const calcualtedDropdownStyle = ref()
const positionElementToTopOfInput = ref(false)

const is12HourFormat = computed(() => props.format === TimeFormatType['12 hour'] || !props.format)
const minimumStartHour = computed(() => {
  if (props.minimumStartTime) {
    return +props.minimumStartTime.split(':')[0]
  }
  return undefined
})
const minimumStartMinute = computed(() => {
  if (props.minimumStartTime) {
    return +props.minimumStartTime.split(':')[1]
  }
  return undefined
})
const maximumStartHour = computed(() => {
  if (!props.maximumStartTime) return
  let hour = +props.maximumStartTime.split(':')[0]

  if (is12HourFormat.value && timeType.value === TimeType.PM && hour > 12) {
    hour = hour - 12
  }
  return hour
})

const maximumStartMinute = computed(() => {
  if (!props.maximumStartTime) return
  return +props.maximumStartTime.split(':')[1]
})

const formatTime = (hour: number, minutes: number) => {
  const formattedHour = hour % 10 === hour ? `0${hour}` : `${hour}`
  const formattedMinute = minutes % 10 === minutes ? `0${minutes}` : `${minutes}`

  return { formattedHour, formattedMinute }
}
const timeToDisplay = computed(() => {
  const { formattedHour, formattedMinute } = formatTime(selectedHour.value, selectedMinute.value)

  if (formattedHour == 'undefined' || formattedMinute == 'undefined') return '...'

  if (is12HourFormat.value) {
    if (formattedHour == '00' && timeType.value == TimeType.PM) {
      return `12:${formattedMinute} ${TimeType[timeType.value]}`
    }
    return `${formattedHour}:${formattedMinute} ${TimeType[timeType.value]}`
  }
  return `${formattedHour}:${formattedMinute}`
})

const updateVModelValue = () => {
  let { formattedHour, formattedMinute } = formatTime(selectedHour.value, selectedMinute.value)

  if (is12HourFormat.value && timeType.value === TimeType.PM) {
    const timeIn24HourFormat = HOURS_IN_DAY / 2 + selectedHour.value
    formattedHour = `${timeIn24HourFormat}`
  }
  if (is12HourFormat.value && timeType.value === TimeType.PM && props.maximumStartTime) {
    if (maximumStartHour.value !== undefined && selectedHour.value > maximumStartHour.value) {
      setSelectedHour(maximumStartHour.value)
    }
    if (
      selectedHour.value === maximumStartHour.value &&
      maximumStartMinute.value &&
      selectedMinute.value > maximumStartMinute.value
    ) {
      setSelectedMinute(maximumStartMinute.value)
    }
    nextTick(() => {
      scrollToActiveTime()
    })
  }

  boundValue.value = `${formattedHour}:${formattedMinute}:00`
}

const setSelectedHour = (hour: number) => {
  let formattedHour = hour
  if (is12HourFormat.value && formattedHour >= 12) {
    timeType.value = TimeType.PM
    const timeIn12HourFormat = formattedHour - HOURS_IN_DAY / 2
    formattedHour = timeIn12HourFormat || 0
  }
  selectedHour.value = formattedHour
  nextTick(updateVModelValue)
}

const setSelectedMinute = (minute: number) => {
  selectedMinute.value = minute
  nextTick(updateVModelValue)
}

const setSelectedTimeType = (type: TimeType) => {
  timeType.value = type
  nextTick(updateVModelValue)
}

const handleTimePickerVisibility = () => {
  if (props.readonly) return
  isTimePickerOpen.value = true
}

const scrollToActiveTime = () => {
  if (!hourRef.value || !minuteRef.value) return
  const hourContainer = hourRef.value
  const minuteContainer = minuteRef.value
  const activeHour = hourRef.value.querySelector('.selected')
  const activeMinute = minuteRef.value.querySelector('.selected')

  const hourContainerPaddingOffset = parseInt(getComputedStyle(hourRef.value).paddingTop)
  const minuteContainerPaddingOffset = parseInt(getComputedStyle(minuteRef.value).paddingTop)
  if (activeHour) {
    const scrollHourBy = activeHour.offsetTop - hourContainerPaddingOffset
    hourContainer.scrollTo({ top: scrollHourBy, behavior: 'smooth' })
  }
  if (activeMinute) {
    const scrollMinuteBy = activeMinute.offsetTop - minuteContainerPaddingOffset
    minuteContainer.scrollTo({ top: scrollMinuteBy, behavior: 'smooth' })
  }
}

watchPostEffect(() => {
  if (minimumStartHour.value && minimumStartHour.value > selectedHour.value) {
    setSelectedHour(minimumStartHour.value)
  }
  if (
    minimumStartMinute.value &&
    minimumStartHour.value &&
    minimumStartHour.value >= selectedHour.value &&
    minimumStartMinute.value > selectedMinute.value
  ) {
    setSelectedMinute(minimumStartMinute.value)
  }
})

watch(
  () => props.maximumStartTime,
  () => {
    if (
      props.maximumStartTime &&
      maximumStartHour.value &&
      selectedHour.value > maximumStartHour.value
    ) {
      setSelectedHour(maximumStartHour.value)
    }
    if (
      selectedHour.value === maximumStartHour.value &&
      maximumStartMinute.value &&
      selectedMinute.value > maximumStartMinute.value
    ) {
      setSelectedMinute(maximumStartMinute.value)
    }
  }
)

watch(isTimePickerOpen, () => {
  nextTick(() => {
    const inputFieldElement = timepickerInputField.value.$el
    const elementHeight = timepickerDropdown.value?.clientHeight
    const { top, bottom, left } = inputFieldElement.getBoundingClientRect()
    const distanceFromWindowBottom = window.innerHeight - top

    let elementPosition: any = {
      zIndex: 9000,
      left: `${left}px`
    }

    if (distanceFromWindowBottom < elementHeight + 70) {
      positionElementToTopOfInput.value = true
      elementPosition = {
        ...elementPosition,
        top: `${top - elementHeight}px`
      }
    } else {
      elementPosition = {
        ...elementPosition,
        top: `${bottom}px`
      }
    }
    calcualtedDropdownStyle.value = elementPosition
  })
  nextTick(() => {
    scrollToActiveTime()
  })
})

const setTime = () => {
  if (boundValue.value === null) {
    boundValue.value = props.defaultStartTime ?? TIME_FORMAT_24_HRS
  }

  nextTick(() => {
    const [hour, minute] = boundValue.value.split(':')

    if (+hour % 10 === 0 && +hour / 10 === 0) {
      setSelectedHour(0)
    } else {
      setSelectedHour(+hour)
    }

    if (+minute % 10 === 0 && +minute / 10 === 0) {
      setSelectedMinute(0)
    } else {
      setSelectedMinute(+minute)
    }
  })
}

const shouldDisablePastHour = (hour: number) => {
  if (props.disablePastTime) {
    const currentTime = moment().format('HH:mm a')
    const [currentHour, currentMinute] = currentTime.split(':')
    if (+hour < +currentHour) {
      return true
    }
  }

  return false
}

const shouldDisablePastMinute = (minute: number) => {
  if (props.disablePastTime) {
    const currentTime = moment().format('HH:mm')
    const [currentHour, currentMinute] = currentTime.split(':')
    if (+selectedHour.value === +currentHour && +minute < +currentMinute) {
      return true
    }
  }

  return false
}

watch(
  () => boundValue.value,
  () => {
    setTime()
    if (boundValue.value === DEFAULT_JOB_START_TIME && timeType.value === TimeType.PM) {
      setSelectedTimeType(TimeType.AM)
    }
  }
)

onMounted(() => {
  setTime()
})
</script>
<template>
  <div class="form-timepicker">
    <FormInputField
      ref="timepickerInputField"
      hide-details
      append-inner-icon="mdi:mdi-clock-time-four-outline"
      v-bind="$attrs"
      :value="timeToDisplay"
      readonly
      :height="$attrs.height ?? DEFAULT_DATETIME_PICKER_HEIGHT"
      :class="`datetime-input ${$attrs.small !== undefined ? 'body-3 medium' : 'inherit--font'}`"
      :show-highlight="!!selectedTime"
      :disabled="disabled"
      @click.stop="handleTimePickerVisibility"
    />
    <Transition
      :name="positionElementToTopOfInput ? 'slide-y-transition' : 'slide-y-reverse-transition'"
    >
      <Teleport to="body">
        <div
          v-if="isTimePickerOpen"
          ref="timepickerDropdown"
          class="timepicker d-flex bg-white"
          :style="calcualtedDropdownStyle"
          v-click-outside="() => (isTimePickerOpen = false)"
        >
          <div
            ref="hourRef"
            class="hours d-flex flex-column ga-1 pa-1 overflow-y--scroll position-relative"
          >
            <BaseButton
              v-for="(hour, index) in is12HourFormat
                ? HOURS_IN_DAY - HOURS_IN_DAY / 2
                : HOURS_IN_DAY"
              height-variant="auto"
              :class="`${selectedHour === hour - 1 ? 'selected' : ''}`"
              :key="index"
              :disabled="
                (minimumStartHour !== undefined && index < minimumStartHour) ||
                (maximumStartHour !== undefined && index > maximumStartHour)
              "
              @click.stop="setSelectedHour(hour - 1)"
              @focusin.stop
              @focusout.stop
            >
              {{ (hour - 1) % 10 === hour - 1 ? 0 : '' }}{{ hour - 1 }}
            </BaseButton>
          </div>
          <div
            ref="minuteRef"
            class="minutes d-flex flex-column ga-1 pa-1 overflow-y--scroll position-relative"
          >
            <span v-for="(minute, index) in MINUTES_IN_HOUR" :key="index">
              <BaseButton
                height-variant="auto"
                :class="`${selectedMinute === minute ? 'selected' : ''}`"
                :disabled="
                  (minimumStartHour !== undefined &&
                    minimumStartMinute !== undefined &&
                    (selectedHour < minimumStartHour ||
                      (selectedHour === minimumStartHour && minute - 1 < minimumStartMinute))) ||
                  (maximumStartHour !== undefined &&
                    maximumStartMinute !== undefined &&
                    (selectedHour > maximumStartHour ||
                      (selectedHour === maximumStartHour && minute - 1 > maximumStartMinute)))
                "
                @click.stop="setSelectedMinute(minute)"
                @focusin.stop
                @focusout.stop
              >
                {{ minute < 10 ? 0 : '' }}{{ minute }}
              </BaseButton>
            </span>
          </div>
          <div v-if="is12HourFormat" class="d-flex flex-column ga-1 pa-1">
            <BaseButton
              height-variant="auto"
              :class="`${timeType === TimeType.AM ? 'selected' : ''}`"
              @click.stop="setSelectedTimeType((timeType = TimeType.AM))"
              @focusin.stop
              @focusout.stop
              >{{ TimeType[TimeType.AM] }}</BaseButton
            >
            <BaseButton
              height-variant="auto"
              :class="`${timeType === TimeType.PM ? 'selected' : ''}`"
              @click.stop="setSelectedTimeType((timeType = TimeType.PM))"
              @focusin.stop
              @focusout.stop
              >{{ TimeType[TimeType.PM] }}</BaseButton
            >
          </div>
        </div>
      </Teleport>
    </Transition>
  </div>
</template>
<style scoped lang="scss"></style>
